/**
 * Impersonation Indicator Component
 * Exibe um ícone no AppBar quando o usuário está impersonando outro usuário
 * Ao clicar, abre um menu (desktop) ou drawer (mobile) com as informações
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
  Badge,
  Tooltip
} from '@mui/material';
import {
  Person as PersonIcon,
  Info as InfoIcon,
  AccessTime as AccessTimeIcon,
  Description as DescriptionIcon,
  SupportAgent as SupportAgentIcon
} from '@mui/icons-material';
import { apiService } from '@services/api';
import InfoBannerDrawer from './InfoBannerDrawer';

interface ImpersonationStatus {
  isImpersonating: boolean;
  impersonator?: {
    userId: string;
    email: string;
  };
  impersonatedUser?: {
    userId: string;
    email: string;
  };
  startedAt?: string;
  expiresAt?: string;
  reason?: string;
}

interface ImpersonationBannerProps {
  isMobile?: boolean;
  isOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

const ImpersonationBanner: React.FC<ImpersonationBannerProps> = ({ 
  isMobile: isMobileProp,
  isOpen = false,
  onOpen,
  onClose
}) => {
  const [status, setStatus] = useState<ImpersonationStatus | null>(null);
  const anchorElRef = useRef<HTMLElement | null>(null);
  const theme = useTheme();
  const isMobileFromTheme = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = isMobileProp ?? isMobileFromTheme;

  const checkImpersonationStatus = useCallback(async () => {
    try {
      const response = await apiService.getImpersonationStatus();
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        setStatus(data);
      } else {
        setStatus({ isImpersonating: false });
      }
    } catch {
      if (!status) {
        setStatus({ isImpersonating: false });
      }
    }
  }, [status]);

  useEffect(() => {
    // Verificar imediatamente ao montar o componente
    checkImpersonationStatus();

    // Escutar evento customizado quando impersonação inicia
    const handleImpersonationStart = () => {
      checkImpersonationStatus();
    };

    // Escutar evento de visibilidade da página (apenas se estiver impersonando)
    const handleVisibilityChange = () => {
      if (!document.hidden && status?.isImpersonating) {
        checkImpersonationStatus();
      }
    };

    window.addEventListener('impersonation:start', handleImpersonationStart);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('impersonation:start', handleImpersonationStart);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Remover dependências para evitar loops

  const handleIconClick = (event: React.MouseEvent<HTMLElement>) => {
    anchorElRef.current = event.currentTarget;
    onOpen?.();
  };

  const handleClose = () => {
    onClose?.();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!status || !status.isImpersonating) {
    return null;
  }

  const content = (
    <>
        {/* Usuário Impersonado */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
            Visualizando como
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon sx={{ color: 'primary.main', fontSize: 20 }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {status.impersonatedUser?.email || status.impersonator?.email || 'usuário impersonado'}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Impersonador */}
        {status.impersonator?.email && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
              Acesso de Suporte por
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SupportAgentIcon sx={{ color: 'primary.main', fontSize: 20 }} />
              <Typography variant="body2">
                {status.impersonator.email}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Datas */}
        {status.startedAt && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                Informações da Sessão
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                <AccessTimeIcon sx={{ color: 'text.secondary', fontSize: 18, mt: 0.25 }} />
                <Box>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                    Iniciado em: {formatDate(status.startedAt)}
                  </Typography>
                  {status.expiresAt && (
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                      Expira em: {formatDate(status.expiresAt)}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </>
        )}

        {/* Motivo */}
        {status.reason && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                Motivo
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <DescriptionIcon sx={{ color: 'text.secondary', fontSize: 18, mt: 0.25 }} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {status.reason}
                </Typography>
              </Box>
            </Box>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Instrução */}
        <Box
          sx={{
            p: 1.5,
            bgcolor: 'action.hover',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary', fontStyle: 'italic' }}>
            Para encerrar a impersonação, use o botão "Sair" no menu do sistema
          </Typography>
        </Box>
    </>
  );

  return (
    <>
      {/* Ícone no AppBar */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mr: { xs: 0.5, sm: 1 } }}>
        <Tooltip title="Modo de Impersonação Ativo" arrow>
          <IconButton
            onClick={handleIconClick}
            color="inherit"
            size="small"
            sx={{
              bgcolor: '#21316a',
              color: 'white',
              '&:hover': {
                bgcolor: '#1a2854'
              }
            }}
          >
            <Badge
              variant="dot"
              color="warning"
              overlap="circular"
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <PersonIcon sx={{ fontSize: 18 }} />
            </Badge>
          </IconButton>
        </Tooltip>
        <Typography
          variant="caption"
          sx={{
            fontSize: { xs: '0.55rem', sm: '0.6rem' },
            color: '#21316a',
            mt: 0.25,
            whiteSpace: 'nowrap',
            lineHeight: 1
          }}
        >
          Imper
        </Typography>
      </Box>

      <InfoBannerDrawer
        isMobile={isMobile}
        anchorEl={isOpen ? anchorElRef.current : null}
        drawerOpen={isOpen && isMobile}
        onClose={handleClose}
        headerIcon={<PersonIcon sx={{ fontSize: 24 }} />}
        headerTitle="Modo de Impersonação"
        headerSubtitle="Sessão ativa"
        headerBackground="linear-gradient(90deg, #f7f8fc 0%, #d1d8e4 100%)"
        headerColor="#21316a"
      >
        {content}
      </InfoBannerDrawer>
    </>
  );
};

export default ImpersonationBanner;
